import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/**
 * SalesPopups — quiet "senior sales manager" coaching cards.
 *
 * The parent (HeroChat) controls WHICH popup should be active by passing the
 * current `trigger` prop. Each unique trigger key is shown at most once per
 * mounted session. Cards auto-dismiss after 10s and can be closed manually.
 *
 * Trigger keys: "welcome" | "process" | "next" | "buying" | "scheduled"
 */

const COPY = {
  en: {
    welcome: {
      eyebrow: "Welcome",
      title: "Welcome to Lone Star Striping.",
      body:
        "Our goal today isn't to sell you. It's to help you make the best decision for your property. We'll walk with you every step of the way.",
    },
    process: {
      eyebrow: "Our Process",
      title: "What makes Lone Star different.",
      body:
        "It isn't just our workmanship — it's our process. Clear communication. Honest recommendations. No surprises. No pressure. Professional results.",
    },
    next: {
      eyebrow: "Here's What Happens Next",
      title: "You're in good hands.",
      body:
        "Once you've selected your striping layout and colors, we coordinate with our office manager and production team, assign your project, confirm scheduling, and keep you informed from start to finish.",
    },
    buying: {
      eyebrow: "Congratulations",
      title: (name) => (name ? `Congratulations, ${name}!` : "Congratulations!"),
      body:
        "The hard part is over. You've done your research. You've asked the right questions. Now let us take care of everything else. We're contacting our office manager and production team to reserve the next available opening and confirm today's pricing. From here on out, we'll guide you through every step. Go enjoy your day — we'll take it from here.",
    },
    scheduled: {
      eyebrow: "You're Scheduled",
      title: "Fantastic.",
      body:
        "Everything is now moving into production. Our office will stay in contact throughout the process. You'll always know exactly what's happening next.",
    },
    close: "Dismiss",
  },
  es: {
    welcome: {
      eyebrow: "Bienvenido",
      title: "Bienvenido a Lone Star Striping.",
      body:
        "Nuestro objetivo hoy no es venderle. Es ayudarle a tomar la mejor decisión para su propiedad. Le acompañamos en cada paso del camino.",
    },
    process: {
      eyebrow: "Nuestro Proceso",
      title: "Lo que nos hace diferentes.",
      body:
        "No es solo nuestro trabajo — es nuestro proceso. Comunicación clara. Recomendaciones honestas. Sin sorpresas. Sin presión. Resultados profesionales.",
    },
    next: {
      eyebrow: "Esto es lo Que Sigue",
      title: "Está en buenas manos.",
      body:
        "Una vez que haya seleccionado su diseño y colores, coordinamos con nuestra oficina y equipo de producción, asignamos su proyecto, confirmamos el horario y le mantenemos informado de principio a fin.",
    },
    buying: {
      eyebrow: "Felicidades",
      title: (name) => (name ? `¡Felicidades, ${name}!` : "¡Felicidades!"),
      body:
        "La parte difícil ya pasó. Ha hecho su investigación. Ha hecho las preguntas correctas. Ahora déjenos encargarnos de todo lo demás. Estamos contactando a nuestra oficina y equipo de producción para reservar el próximo espacio disponible y confirmar el precio de hoy. A partir de aquí, le guiaremos en cada paso. Disfrute su día — nosotros nos encargamos.",
    },
    scheduled: {
      eyebrow: "Está Agendado",
      title: "Fantástico.",
      body:
        "Todo está entrando a producción. Nuestra oficina se mantendrá en contacto durante todo el proceso. Siempre sabrá exactamente lo que sigue.",
    },
    close: "Cerrar",
  },
};

export default function SalesPopups({ trigger, visitorName, isSpanish = false }) {
  const [active, setActive] = useState(null);
  const [shown, setShown] = useState(() => new Set());
  const lang = isSpanish ? "es" : "en";
  const t = COPY[lang];

  useEffect(() => {
    if (!trigger) return;
    if (shown.has(trigger)) return;
    setActive(trigger);
    setShown((s) => new Set(s).add(trigger));
    // Auto-dismiss after 10s
    const id = setTimeout(() => {
      setActive((cur) => (cur === trigger ? null : cur));
    }, 10000);
    return () => clearTimeout(id);
  }, [trigger, shown]);

  const content = useMemo(() => {
    if (!active || !t[active]) return null;
    const raw = t[active];
    const title = typeof raw.title === "function" ? raw.title(visitorName) : raw.title;
    return { eyebrow: raw.eyebrow, title, body: raw.body };
  }, [active, t, visitorName]);

  if (!content) return null;

  const node = (
    <div
      role="status"
      aria-live="polite"
      data-testid={`sales-popup-${active}`}
      style={{
        position: "fixed",
        top: 24,
        right: 24,
        zIndex: 9999,
        maxWidth: 380,
        width: "calc(100vw - 32px)",
        borderRadius: 8,
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(17,17,17,0.97) 0%, rgba(28,24,14,0.98) 100%)",
        border: "1px solid rgba(212,175,55,0.4)",
        boxShadow:
          "0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.1), inset 0 1px 0 rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",
        animation: "sales-popup-in 380ms cubic-bezier(0.2, 0.9, 0.25, 1)",
      }}
    >
      {/* Gold hairline top */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background:
            "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.85) 50%, transparent 100%)",
        }}
      />

      {/* Close */}
      <button
        type="button"
        aria-label={t.close}
        data-testid={`sales-popup-close-${active}`}
        onClick={() => setActive(null)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 2,
          width: 26,
          height: 26,
          borderRadius: 999,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(212,175,55,0.25)",
          color: "rgba(245,245,240,0.65)",
          cursor: "pointer",
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 13,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 200ms ease, background 200ms ease, border-color 200ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "#F0D580";
          e.currentTarget.style.background = "rgba(212,175,55,0.12)";
          e.currentTarget.style.borderColor = "rgba(212,175,55,0.55)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "rgba(245,245,240,0.65)";
          e.currentTarget.style.background = "rgba(255,255,255,0.04)";
          e.currentTarget.style.borderColor = "rgba(212,175,55,0.25)";
        }}
      >
        ×
      </button>

      <div style={{ padding: "18px 22px 20px 22px" }}>
        <div
          style={{
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#C9A24B",
            marginBottom: 8,
          }}
        >
          {content.eyebrow}
        </div>
        <div
          style={{
            fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif",
            fontWeight: 800,
            fontSize: 18,
            lineHeight: 1.25,
            letterSpacing: "-0.01em",
            color: "#FFFFFF",
            marginBottom: 10,
          }}
        >
          {content.title}
        </div>
        <div
          style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "rgba(245,245,240,0.82)",
          }}
        >
          {content.body}
        </div>
      </div>

      <style>{`
        @keyframes sales-popup-in {
          0% { opacity: 0; transform: translateY(-12px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 640px) {
          [data-testid^="sales-popup-"] {
            top: 12px !important;
            right: 12px !important;
            left: 12px !important;
            max-width: none !important;
            width: auto !important;
          }
        }
      `}</style>
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
